import React from "react";
import { Lightning, ArrowsClockwise, Fingerprint, Ghost, SlidersHorizontal, CaretDown, CaretUp, ArrowRight } from "@phosphor-icons/react";
import { STACK_PRESETS, stackPresetOf } from "@/lib/rawStacks";

// Sprint 68 — compact rules bar: the handful of rules 90% of builds
// need, across the top; everything else lives in the Advanced drawer.
const num = (v, fb = "") => (v === "" || v == null ? fb : v);

const Cell = ({ label, hint, children, testid, className = "" }) => (
  <label className={`flex flex-col gap-1 min-w-[84px] ${className}`} data-testid={testid} title={hint}>
    <span className="mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">{label}</span>
    {children}
  </label>
);

const inputCls = "border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 mono text-[12px] w-full";
const inputStyle = { background: "#000", color: "#fff" };

const Toggle = ({ on, onClick, icon: Icon, label, hint, testid, accent = "#39FF14" }) => (
  <button
    type="button" onClick={onClick} data-testid={testid} title={hint} aria-pressed={on}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border mono text-[10px] uppercase tracking-widest transition-colors ${on ? "text-black font-bold" : "border-zinc-700 text-zinc-500 hover:text-zinc-200"}`}
    style={on ? { background: accent, borderColor: accent } : undefined}
  >
    <Icon size={13} weight={on ? "fill" : "regular"} />
    {label}
    <span className={`ml-1 w-1.5 h-1.5 rounded-full ${on ? "bg-black" : "bg-zinc-700"}`} />
  </button>
);

const FLEX_POS = ["RB", "WR", "TE"];
const flexBoundsOf = (cfg) => {
  const out = {};
  for (const p of FLEX_POS) {
    const v = (cfg?.flex_bounds || {})[p] || {};
    out[p] = { min: v.min ?? 0, max: v.max ?? 100 };
  }
  return out;
};
export const flexBoundsIssue = (cfg) => {
  const b = flexBoundsOf(cfg);
  for (const p of FLEX_POS) if (Number(b[p].min) > Number(b[p].max)) return `FLEX ${p}: min above max`;
  const sMin = FLEX_POS.reduce((s, p) => s + Number(b[p].min), 0);
  const sMax = FLEX_POS.reduce((s, p) => s + Number(b[p].max), 0);
  if (sMin > 100) return `FLEX minimums add up to ${sMin}% — must be ≤ 100%`;
  if (sMax < 100) return `FLEX maximums add up to ${sMax}% — must be ≥ 100%`;
  return null;
};
const miniCls = "border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-1.5 mono text-[11px] w-[42px] text-center";

// FLEX slot share per position — min/max % of lineups whose FLEX is an RB / WR / TE.
const FlexBounds = ({ cfg, set }) => {
  const b = flexBoundsOf(cfg);
  const issue = flexBoundsIssue(cfg);
  const upd = (p, k, raw) => {
    const v = raw === "" ? (k === "max" ? 100 : 0) : Math.max(0, Math.min(100, +raw || 0));
    set({ flex_bounds: { ...b, [p]: { ...b[p], [k]: v } } });
  };
  return (
    <div className="flex flex-col gap-1" data-testid="rules-bar-flex" title="Share of your lineups whose FLEX slot is a 3rd RB / 4th WR / 2nd TE. Min–max %; leave 0–100 for no rule.">
      <span className={`mono text-[9px] uppercase tracking-[0.2em] whitespace-nowrap ${issue ? "text-[#FFCC00]" : "text-zinc-500"}`}>
        Flex share % · min–max{issue ? ` · ${issue}` : ""}
      </span>
      <div className="flex items-center gap-2">
        {FLEX_POS.map((p) => (
          <div key={p} className="flex items-center gap-1" data-testid={`rules-bar-flex-${p}`}>
            <span className="mono text-[10px] font-black text-zinc-300 w-[20px]">{p}</span>
            <input type="number" min="0" max="100" className={miniCls} style={inputStyle} value={b[p].min}
                   onChange={(e) => upd(p, "min", e.target.value)} data-testid={`rules-bar-flex-${p}-min`} aria-label={`FLEX ${p} min %`} />
            <span className="text-zinc-600 mono text-[10px]">–</span>
            <input type="number" min="0" max="100" className={miniCls} style={inputStyle} value={b[p].max}
                   onChange={(e) => upd(p, "max", e.target.value)} data-testid={`rules-bar-flex-${p}-max`} aria-label={`FLEX ${p} max %`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const RawRulesBar = ({ cfg, setCfg, onGenerate, busy, disabled, disabledReason, advancedOpen, onToggleAdvanced, advancedSeen = true, showdown = false }) => {
  const set = (patch) => setCfg({ ...cfg, ...patch });
  const stackPreset = stackPresetOf(cfg);
  const applyStack = (v) => set(STACK_PRESETS[v] || STACK_PRESETS.none);
  const pulse = !advancedSeen && !advancedOpen;
  return (
    <section data-testid="raw-rules-bar" className="border border-zinc-800 bg-[#0d0d0d] px-4 py-3 mb-4 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14]/60 to-transparent" />
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <Cell label="Lineups" testid="rules-bar-lineups" className="w-[84px]">
          <input type="number" min="1" max="150" className={inputCls} style={inputStyle} value={cfg.num_lineups}
                 onChange={(e) => set({ num_lineups: Math.max(1, Math.min(150, +e.target.value || 0)) })} data-testid="rules-bar-lineups-input" />
        </Cell>
        <Cell label="Salary min" hint="Lowest total salary a lineup may spend" testid="rules-bar-salary-min" className="w-[96px]">
          <input type="number" step="100" placeholder={showdown ? "—" : "45000"} className={inputCls} style={inputStyle} value={num(cfg.min_salary)}
                 onChange={(e) => set({ min_salary: e.target.value === "" ? "" : +e.target.value })} data-testid="rules-bar-salary-min-input" />
        </Cell>
        <Cell label="Salary max" hint="Salary cap for every lineup (DK max 50,000)" testid="rules-bar-salary-max" className="w-[96px]">
          <input type="number" step="100" placeholder="50000" className={inputCls} style={inputStyle} value={num(cfg.max_salary)}
                 onChange={(e) => set({ max_salary: e.target.value === "" ? "" : Math.min(50000, +e.target.value) })} data-testid="rules-bar-salary-max-input" />
        </Cell>
        <Cell label="Max exp %" hint="Default cap on how many lineups any one player can be in (per-player caps in the pool win)" testid="rules-bar-max-exposure" className="w-[84px]">
          <input type="number" min="1" max="100" placeholder="100" className={inputCls} style={inputStyle} value={num(cfg.global_max_exposure)}
                 onChange={(e) => set({ global_max_exposure: e.target.value === "" ? "" : Math.max(1, Math.min(100, +e.target.value)) })} data-testid="rules-bar-max-exposure-input" />
        </Cell>
        <Cell label="Min unique" hint="Fewest different players between any two lineups" testid="rules-bar-min-unique" className="w-[84px]">
          <input type="number" min="0" max="8" className={inputCls} style={inputStyle} value={cfg.min_uniqueness}
                 onChange={(e) => set({ min_uniqueness: Math.max(0, Math.min(8, +e.target.value || 0)) })} data-testid="rules-bar-min-unique-input" />
        </Cell>
        {!showdown && (
          <>
            <div className="flex flex-col gap-1" data-testid="rules-bar-stack">
              <span className="mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">QB stack</span>
              <select className={`${inputCls} w-[168px]`} style={inputStyle} value={stackPreset} onChange={(e) => applyStack(e.target.value)} data-testid="rules-bar-stack-select"
                      title="Naked QB = no stack rule · QB + 1 / QB + 2 = the QB's own WR/TE · Custom mix = set a min/max share per stack type in Advanced">
                <option value="none">Naked QB · no stack</option>
                <option value="qb1">QB + 1 own WR/TE</option>
                <option value="qb2">QB + 2 own WR/TE</option>
                <option value="mix">Custom mix · min/max</option>
              </select>
              <button type="button" onClick={onToggleAdvanced} data-testid="rules-bar-stack-advanced-note"
                      className="mono text-[9px] text-left text-zinc-500 hover:text-[#39FF14] inline-flex items-center gap-1 whitespace-nowrap">
                Stack min / max % → <span className="text-[#39FF14]">Advanced</span> <ArrowRight size={9} />
              </button>
            </div>
            <Cell label="Bring-back" hint="Add one opposing WR/TE from the QB's game to every stacked lineup" testid="rules-bar-bring-back" className="w-[70px]">
              <button type="button" onClick={() => set({ bring_back: !cfg.bring_back })} aria-pressed={!!cfg.bring_back} data-testid="rules-bar-bring-back-toggle"
                      className={`mono text-[11px] px-2 py-1.5 border ${cfg.bring_back ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-800 text-zinc-500"}`}>
                {cfg.bring_back ? "ON" : "OFF"}
              </button>
            </Cell>
            <Cell label="Per-team max" hint="Most players from ONE team inside a single lineup (DK allows up to 8 — you can never roster all 9 from one team). Default 4 keeps stacks disciplined; nothing here limits how many teams you draw from." testid="rules-bar-team-max" className="w-[92px]">
              <input type="number" min="1" max="8" placeholder="4" className={inputCls} style={inputStyle} value={num(cfg.max_per_team)}
                     onChange={(e) => set({ max_per_team: e.target.value === "" ? "" : Math.max(1, Math.min(8, +e.target.value)) })} data-testid="rules-bar-team-max-input" />
            </Cell>
            <FlexBounds cfg={cfg} set={set} />
          </>
        )}
        <div className="flex items-end gap-2 ml-auto flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Edge layers</span>
            <div className="flex gap-1.5">
              <Toggle on={!!cfg.fingerprint_variance_enabled} onClick={() => set({ fingerprint_variance_enabled: !cfg.fingerprint_variance_enabled })}
                      icon={Fingerprint} label="Fingerprint" testid="rules-bar-fingerprint"
                      hint="Unique Seed — your builds get a private seed so no two subscribers ship identical lineups" />
              <Toggle on={!!cfg.cluster_rotation_enabled} onClick={() => set({ cluster_rotation_enabled: !cfg.cluster_rotation_enabled })}
                      icon={ArrowsClockwise} label="Rotation" testid="rules-bar-rotation"
                      hint="Peer Swap — swaps near-identical peers (same position, similar salary & projection) across your lineups" />
              <Toggle on={!!cfg.shadow_field_enabled} onClick={() => set({ shadow_field_enabled: !cfg.shadow_field_enabled })}
                      icon={Ghost} label="Shadow field" testid="rules-bar-shadow" accent="#FF8A00"
                      hint="Shadow Field — rebuilds the lineups the field's optimizers produce from your 3 ingested projection CSVs and rejects any build sharing 7+ of 9 players with them, plus a duplicate-probability cap from consensus ownership. OFF = pure points-per-dollar build." />
            </div>
          </div>
          <button type="button" onClick={onToggleAdvanced} data-testid="rules-bar-advanced-toggle" aria-expanded={advancedOpen} data-pulse={pulse ? "1" : "0"}
                  style={pulse ? { color: "#39FF14", borderColor: "#39FF14", background: "transparent" }
                    : { color: advancedOpen ? "#ffffff" : "#a1a1aa", borderColor: advancedOpen ? "#d4d4d8" : "#3f3f46", background: advancedOpen ? "#27272a" : "transparent" }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border mono text-[10px] uppercase tracking-widest hover:opacity-90 ${pulse ? "raw-adv-pulse" : ""}`}>
            <SlidersHorizontal size={13} /> Advanced {advancedOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </button>
          <button type="button" onClick={onGenerate} disabled={busy || disabled} data-testid="rules-bar-generate" title={disabled ? disabledReason : undefined}
                  className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-30 text-black font-black uppercase tracking-widest text-[11px] px-5 py-2">
            <Lightning size={14} weight="fill" />
            {busy ? "Optimizing…" : "Generate"}
          </button>
        </div>
      </div>
      <div className="mono text-[9px] text-zinc-600 mt-2" data-testid="rules-bar-mode-line">
        {cfg.shadow_field_enabled
          ? <span><span className="text-[#FF8A00]">Shadow field ON</span> · dodging the field's optimizer builds (≤6 shared players) and capping duplicate probability</span>
          : <span><span className="text-zinc-300">Shadow field OFF</span> · pure points-per-dollar build{cfg.fingerprint_variance_enabled || cfg.cluster_rotation_enabled ? ", plus your edge layers" : ""}</span>}
      </div>
    </section>
  );
};

export default RawRulesBar;
